import { StickyNote } from 'lucide-react';
import { useState } from 'react';

import { useCapability } from '../../../../hooks/use-capability';
import { useCreateInternalNote, useInternalNotes } from '../../../../hooks/use-internal-notes';
import { useAuthStore } from '../../../../stores/auth-store';
import { relativeTime } from '../../format';

import { PanelSection } from './PanelSection';

import type { InternalNoteWireItem } from '../../../../lib/api-client';
import type { ConversationDetail } from '../../../../lib/types';

type Visibility = 'public_team' | 'private_author' | 'private_supervisors';

/**
 * Aba "Notas" — InternalNote (Fatia G — ADR-049 + Caderno cap 39).
 *
 * Lista + cria notas internas com 3 visibilidades canônicas:
 *   - public_team        → todo o time (default; alinha Caderno cap 39
 *                          "equipe opera como equipe").
 *   - private_supervisors → apenas supervisores+ (gestor/admin/dpo).
 *   - private_author      → apenas o autor.
 *
 * Capability gate `peer-coordination-internal-notes` (Team+). Sem
 * capability o textarea fica desabilitado e a lista renderiza placeholder
 * (defense-in-depth — backend valida `@RequiresCapability(...)` no controller).
 *
 * Backend filtra a lista por role/permissions: o que retorna do server já
 * está filtrado para o requester (server-side authoritative).
 */
export function TabNotas({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  const hasCap = useCapability('peer-coordination-internal-notes');
  const conversationId = conversation.id;
  const notesQuery = useInternalNotes(conversationId);
  const createMutation = useCreateInternalNote(conversationId);
  const currentUserId = useAuthStore((s) => s.user?.id) ?? null;

  return (
    <div className="flex flex-col">
      <PanelSection title="Notas internas" eyebrow="não chegam ao cliente">
        {!hasCap && (
          <p className="placeholder-text">
            Sua função não tem permissão para visualizar ou criar notas internas.
          </p>
        )}

        {hasCap && (
          <>
            <Composer
              conversationId={conversationId}
              disabled={createMutation.isPending}
              onSubmit={(payload, callbacks) => {
                createMutation.mutate(payload, {
                  // react-query invalida ['internal-notes', id] via hook; o Composer
                  // limpa o rascunho só aqui (sucesso) e preserva o texto em erro.
                  onSuccess: callbacks.onSuccess,
                  onError: callbacks.onError,
                });
              }}
            />

            <NotesList
              notes={notesQuery.data}
              isLoading={notesQuery.isLoading}
              isError={notesQuery.isError}
              currentUserId={currentUserId}
            />
          </>
        )}

        <p
          style={{
            fontSize: 10,
            color: 'var(--fg-3)',
            fontFamily: 'var(--font-mono)',
            margin: 0,
          }}
        >
          Notas internas NÃO chegam ao cliente. Default: visível ao time.
        </p>
      </PanelSection>
    </div>
  );
}

interface ComposerProps {
  conversationId: string;
  disabled: boolean;
  onSubmit: (
    payload: { content: string; visibility: Visibility },
    callbacks: { onSuccess: () => void; onError: () => void },
  ) => void;
}

/** Chave do rascunho não-salvo por conversa (decisão 1 — preservação local). */
function draftKey(conversationId: string): string {
  return `lume:note-draft:${conversationId}`;
}

function readDraft(conversationId: string): string {
  try {
    return window.localStorage.getItem(draftKey(conversationId)) ?? '';
  } catch {
    return '';
  }
}

function writeDraft(conversationId: string, content: string): void {
  try {
    if (content.length === 0) {
      window.localStorage.removeItem(draftKey(conversationId));
    } else {
      window.localStorage.setItem(draftKey(conversationId), content);
    }
  } catch {
    // storage indisponível (modo privado/quota) — degrada sem rascunho.
  }
}

/**
 * Composer mínimo de InternalNote — textarea + visibility selector + submit.
 *
 * Texto digitado e não salvo persiste localmente POR CONVERSA (rascunho) —
 * trocar de conversa/aba ou recarregar não perde o texto. A nota só nasce
 * no Salvar (modelo de múltiplas notas ADR-049 intocado); o submit limpa
 * o rascunho.
 *
 * Optimistic update fica no `useCreateInternalNote` (invalidate query no
 * sucesso). Revert em erro vem do react-query (retry default off + erro
 * mostra mantém estado local com mensagem inline).
 */
function Composer({ conversationId, disabled, onSubmit }: ComposerProps): JSX.Element {
  const [content, setContentState] = useState(() => readDraft(conversationId));
  const [visibility, setVisibility] = useState<Visibility>('public_team');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function setContent(value: string): void {
    setContentState(value);
    writeDraft(conversationId, value);
  }

  function handleSubmit(): void {
    const trimmed = content.trim();
    if (trimmed.length === 0) {
      setErrorMsg('Digite o conteúdo da nota antes de salvar.');
      return;
    }
    setErrorMsg(null);
    // Limpa o rascunho SÓ no sucesso — em falha o texto digitado é preservado
    // (bug da revisão: antes limpava antes do resultado ⇒ perda de conteúdo).
    onSubmit(
      { content: trimmed, visibility },
      {
        onSuccess: () => {
          setContent('');
          setVisibility('public_team');
        },
        onError: () => {
          setErrorMsg('Não foi possível salvar a nota. Tente novamente — seu texto foi preservado.');
        },
      },
    );
  }

  return (
    <div className="space-y-2">
      <textarea
        disabled={disabled}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Nota interna — visível ao time por padrão."
        className="input w-full text-xs"
        style={{ minHeight: 120, background: 'var(--bg-canvas)', resize: 'vertical' }}
      />
      <div className="flex items-center gap-2">
        <label htmlFor="note-visibility" className="text-2xs text-text-secondary">
          Visibilidade
        </label>
        <select
          id="note-visibility"
          disabled={disabled}
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as Visibility)}
          className="input text-xs flex-1"
        >
          <option value="public_team">Time</option>
          <option value="private_supervisors">Supervisores</option>
          <option value="private_author">Só eu</option>
        </select>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || content.trim().length === 0}
          className="btn btn--primary btn--sm"
        >
          {disabled ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
      {content.trim().length > 0 && (
        <p
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: 'var(--fg-3)',
            margin: 0,
          }}
        >
          rascunho preservado — vira nota ao Salvar
        </p>
      )}
      {errorMsg !== null && (
        /* Fase C Brief 2A r7b — DS danger token. */
        <p className="text-2xs" style={{ color: 'var(--danger-fg)' }} role="alert">
          {errorMsg}
        </p>
      )}
    </div>
  );
}

interface NotesListProps {
  notes: InternalNoteWireItem[];
  isLoading: boolean;
  isError: boolean;
  currentUserId: string | null;
}

function NotesList({ notes, isLoading, isError, currentUserId }: NotesListProps): JSX.Element {
  if (isLoading) {
    return <p className="placeholder-text">Carregando notas…</p>;
  }
  if (isError) {
    return <p className="placeholder-text">Não foi possível carregar as notas.</p>;
  }
  if (notes.length === 0) {
    return (
      <div className="empty-mini">
        <StickyNote className="icon" size={14} aria-hidden />
        Nenhuma nota ainda.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {notes.map((note) => (
        <NoteCard key={note.id} note={note} isMine={note.authorUserId === currentUserId} />
      ))}
    </ul>
  );
}

function NoteCard({ note, isMine }: { note: InternalNoteWireItem; isMine: boolean }): JSX.Element {
  return (
    <li className="rounded border border-border-subtle bg-surface-elevated p-2 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <VisibilityBadge visibility={note.visibility} />
        <span className="text-2xs text-text-secondary opacity-70">
          {relativeTime(note.createdAt)}
          {isMine ? ' · você' : ''}
        </span>
      </div>
      <p className="text-xs text-text-primary whitespace-pre-wrap break-words">{note.content}</p>
    </li>
  );
}

function VisibilityBadge({ visibility }: { visibility: Visibility }): JSX.Element {
  // Fase C Brief 2A r7b — DS tokens via inline style (Spec §1.5):
  //   public_team        → info-bg/info-fg/info-border    (Time visível a todos)
  //   private_supervisors → warning-bg/fg/border           (Supervisores only)
  //   private_author      → bg-surface-2 + fg-2 + border-default (Só autor)
  const map: Record<Visibility, { label: string; style: React.CSSProperties }> = {
    public_team: {
      label: 'Time',
      style: {
        background: 'var(--info-bg)',
        color: 'var(--info-fg)',
        borderColor: 'var(--info-border)',
      },
    },
    private_supervisors: {
      label: 'Supervisores',
      style: {
        background: 'var(--warning-bg)',
        color: 'var(--warning-fg)',
        borderColor: 'var(--warning-border)',
      },
    },
    private_author: {
      label: 'Só você',
      style: {
        background: 'var(--bg-surface-2)',
        color: 'var(--fg-2)',
        borderColor: 'var(--border-default)',
      },
    },
  };
  const { label, style } = map[visibility];
  return (
    <span
      className="text-2xs px-1.5 py-0.5 rounded border uppercase tracking-wide font-medium"
      style={style}
    >
      {label}
    </span>
  );
}
